// Credit pricing for the video editor. Single source of truth — every
// "Generar" button reads its cost from here and every guard checks the
// user's balance against this number.
//
// Conversion: 1 credit ≈ $0.08 USD (because 1 image generation = 1 credit
// and an image costs ~8¢ at the provider). All costs round UP to the next
// whole credit, with a hard minimum of 1 — no action is ever free.
//
// To recalibrate prices: change the constants in this file, NOT the
// per-button math at the call sites.

export type VideoModel = 'fast' | 'standard';
export type VideoResolution = '480p' | '720p' | '1080p';

// ── Per-second / per-char rates ──────────────────────────────────────────

// General-mode (Seedance 2 / Seedance 2 Fast). Per second of clip duration.
// Standard 480p está intencionalmente subvaluado (1 cr/s en lugar de ~1.1) para
// mantener el tier de entrada atractivo. 1080p también está intencionalmente
// subvaluado (3 cr/s en lugar de ~6) por decisión de producto: el costo real
// del proveedor es ~$0.48/s pero cobramos $0.24/s para mantener el pricing
// alineado al pedido del jefe de "1-2 cr/s, máximo 3". Es deliberado.
const VIDEO_GENERAL_CR_PER_SEC: Record<VideoModel, Record<VideoResolution, number>> = {
  fast: {
    '480p': 1,
    '720p': 2,
    '1080p': 3,
  },
  standard: {
    '480p': 1,
    '720p': 2,
    '1080p': 3,
  },
};

// Dialog-mode video (HeyGen / talking heads). Provider charges the same
// $0.06/s regardless of resolution. We keep the FRACTIONAL rate here and
// only round once — at the end of `creditsForVideoDialog`, after summing
// with the voice cost. Rounding the rate itself (e.g. to 1 cr/s) and then
// ceil-ing the sum overcharges by a whole credit on every short clip.
//
//   $0.06 per second / $0.08 per credit = 0.75 credits/second
const VIDEO_DIALOG_CR_PER_SEC = 0.06 / 0.08;

// TTS (voice) — $0.30 per 1000 chars = 0.00375 credits/char.
//
//   $0.30 per 1000 chars / $0.08 per credit = 0.00375 credits/char
const VOICE_CR_PER_CHAR = 0.3 / 1000 / 0.08;

// One generated song (instrumental or with vocals) — $0.12 → 1.5 cr → 2.
const SONG_CR = 2;

// Traducción de video con lip-sync (HeyGen Video Translate v3).
// Costo real medido en HeyGen: ~$0.0036/seg → ~0.045 cr/s.
// Tarifa de producto: 0.3 cr/s → margen ~6.7× sobre el costo real.
// Es feature premium pero accesible: video de 15s cuesta 5 créditos
// (~$0.40 para el usuario) en lugar de los 30 cr previos.
// Siempre con mínimo 1 crédito (ceilMin1) — sin freebies.
const VIDEO_TRANSLATE_CR_PER_SEC = 0.3;

// Per image generation OR edit — flat 1 credit.
const IMAGE_CR = 1;

// Used to project dialog-clip duration from text length. ≈ 12.5 chars/sec
// at 1× speech speed for Spanish prose; the multiplier scales linearly. If
// the TTS engine changes pacing, update this constant.
const CHARS_PER_SEC_AT_1X = 12.5;

// ── Helpers ──────────────────────────────────────────────────────────────

const ceilMin1 = (n: number): number =>
  Math.max(1, Math.ceil(Number.isFinite(n) ? n : 0));

// Exposed because the AI-scene panel also wants to show the projected
// duration of a dialog clip alongside its credit cost.
export function estimateDialogDurationSec(
  chars: number,
  speed: number,
): number {
  const safeSpeed = Math.max(0.1, speed || 1);
  const cps = CHARS_PER_SEC_AT_1X * safeSpeed;
  return chars > 0 ? chars / cps : 0;
}

const modelFromQuality = (q?: 'rapido' | 'estandar' | string): VideoModel =>
  q === 'rapido' ? 'fast' : 'standard';

const safeResolution = (r?: string): VideoResolution =>
  r === '480p' || r === '720p' || r === '1080p' ? r : '720p';

// ── Public API ───────────────────────────────────────────────────────────

export function creditsForVideoGeneral(opts: {
  durationSec: number;
  model: VideoModel;
  resolution: VideoResolution;
}): number {
  const rate = VIDEO_GENERAL_CR_PER_SEC[opts.model]?.[opts.resolution] ?? 0;
  if (rate <= 0) return 1;
  return ceilMin1((opts.durationSec || 0) * rate);
}

export function creditsForVideoDialog(opts: {
  chars: number;
  speed: number; // aiSpeechSpeed (0.5 .. 1.5)
}): number {
  const dur = estimateDialogDurationSec(opts.chars, opts.speed);
  const videoCost = dur * VIDEO_DIALOG_CR_PER_SEC;
  const audioCost = (opts.chars || 0) * VOICE_CR_PER_CHAR;
  return ceilMin1(videoCost + audioCost);
}

export function creditsForVoice(chars: number): number {
  return ceilMin1(Math.max(0, chars || 0) * VOICE_CR_PER_CHAR);
}

export function creditsForSong(): number {
  return SONG_CR;
}

export function creditsForImage(): number {
  return IMAGE_CR;
}

export function creditsForVideoTranslate(opts: {
  durationSec: number;
}): number {
  return ceilMin1((opts.durationSec || 0) * VIDEO_TRANSLATE_CR_PER_SEC);
}

// Upscaling de video (Topaz vía Replicate, modelo topazlabs/video-upscale). El
// modelo genera a una RESOLUCIÓN DESTINO (720p/1080p/4k), no por factor de
// escala. La tarifa es por segundo según esa resolución, ~2x de margen sobre el
// costo de Replicate (a $0.08/crédito): 720p ~$0.01/s, 1080p ~$0.02/s, 4k
// ~$0.08/s. ESPEJO del plugin (premiere-plugin/src/lib/credits.ts). Ver ADR-596.
export type UpscaleResolution = '720p' | '1080p' | '4k';
const UPSCALE_CR_PER_SEC: Record<UpscaleResolution, number> = {
  '720p': 0.5,
  '1080p': 0.5,
  '4k': 2,
};

export function creditsForVideoUpscale(opts: {
  durationSec: number;
  targetResolution: UpscaleResolution;
}): number {
  const rate = UPSCALE_CR_PER_SEC[opts.targetResolution] ?? 2;
  return ceilMin1((opts.durationSec || 0) * rate);
}

// Multi-scene = multiple per-segment generations concatenated. We charge
// the same rate as a single contiguous video of the summed duration —
// upstream cost scales linearly so this matches reality.
export function creditsForMultiSceneVideo(opts: {
  scenes: Array<{ duration: number }>;
  model: VideoModel;
  resolution: VideoResolution;
}): number {
  const total = opts.scenes.reduce((acc, s) => acc + (s.duration || 0), 0);
  return creditsForVideoGeneral({
    durationSec: total,
    model: opts.model,
    resolution: opts.resolution,
  });
}

// ── Convenience: cost from a stored clip ─────────────────────────────────
//
// The timeline draft "Generar" buttons (one per draft AI clip) need to
// derive cost from the clip's persisted fields, not from any panel
// state — the panel may be closed or showing a different clip's data.
// We accept a structural subset so this util doesn't depend on the
// editor's TimelineClip type directly.

export interface CreditClipShape {
  type: 'video' | 'image' | 'audio' | 'text' | 'ai-generated' | 'animation';
  duration: number;
  aiPrompt?: string;
  aiDialogue?: string;
  aiSpeechSpeed?: number;
  aiSceneMode?: 'general' | 'personas';
  aiPersonType?: 'static' | 'motion';
  aiAudioType?: 'voice' | 'music';
  aiQuality?: 'rapido' | 'estandar';
  aiResolution?: '480p' | '720p' | '1080p';
  aiMultiEnabled?: boolean;
  aiMultiScenes?: Array<{ duration: number }>;
}

export function creditsForAiClip(clip: CreditClipShape): number {
  if (clip.type === 'audio') {
    if (clip.aiAudioType === 'music') return creditsForSong();
    const chars = (clip.aiPrompt || clip.aiDialogue || '').length;
    return creditsForVoice(chars);
  }
  if (clip.type !== 'ai-generated') return 1;

  if (clip.aiSceneMode === 'personas') {
    // Same quirk as the live panel: static personas keeps the spoken text
    // in `aiPrompt`, motion personas keeps it in `aiDialogue` (with
    // `aiPrompt` reserved for the visual prompt). Use whichever the clip
    // actually has — fall back across both so a partially-populated draft
    // never bills 0.
    const dialogText =
      clip.aiPersonType === 'motion'
        ? (clip.aiDialogue || clip.aiPrompt || '')
        : (clip.aiPrompt || clip.aiDialogue || '');
    return creditsForVideoDialog({
      chars: dialogText.length,
      speed: clip.aiSpeechSpeed ?? 1.1,
    });
  }

  const model = modelFromQuality(clip.aiQuality);
  const resolution = safeResolution(clip.aiResolution);

  if (clip.aiMultiEnabled && Array.isArray(clip.aiMultiScenes) && clip.aiMultiScenes.length > 0) {
    return creditsForMultiSceneVideo({
      scenes: clip.aiMultiScenes,
      model,
      resolution,
    });
  }

  return creditsForVideoGeneral({
    durationSec: clip.duration || 0,
    model,
    resolution,
  });
}
